/**
 * History Store
 * Manages recently played history view state including pagination and track selection
 */

import { create } from 'zustand';
import type { Track } from '../api/types';

/**
 * Play history item with timestamp and context
 */
export interface PlayHistory {
  track: Track;
  playedAt: string; // ISO 8601 timestamp (camelCase from backend)
  context: PlayContext | null;
}

/**
 * Context information for where the track was played (playlist, album, etc.)
 */
export interface PlayContext {
  type: 'playlist' | 'album' | 'artist' | 'collection';
  href: string;
  external_urls: {
    spotify: string;
  };
  uri: string;
}

/**
 * Pagination cursors for navigating history pages
 */
export interface PaginationCursors {
  before?: string;
  after?: string;
}

/**
 * Current page of history data with items and pagination cursors
 */
export interface CurrentPage {
  items: PlayHistory[];
  cursors: PaginationCursors;
}

/**
 * History store state interface
 */
export interface HistoryStore {
  // State
  currentPage: CurrentPage;
  selectedTracks: Set<string>; // Track IDs
  selectedDate: Date | null; // Filter to specific date (null = show all)
  isLoading: boolean;
  error: string | null;

  // Actions
  setCurrentPage: (page: CurrentPage) => void;
  toggleTrackSelection: (trackId: string) => void;
  selectAll: () => void;
  clearSelection: () => void;
  setSelectedDate: (date: Date | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

/**
 * Default history page state
 */
const defaultCurrentPage: CurrentPage = {
  items: [],
  cursors: {
    before: undefined,
    after: undefined,
  },
};

/**
 * History store for managing recently played history view
 */
export const useHistoryStore = create<HistoryStore>((set, get) => ({
  // Initial state
  currentPage: defaultCurrentPage,
  selectedTracks: new Set<string>(),
  selectedDate: null,
  isLoading: false,
  error: null,

  // Set current page data and pagination cursors
  setCurrentPage: (page) => {
    set({
      currentPage: page,
      error: null, // Clear any previous errors when new page is loaded
    });
  },

  // Toggle selection of a single track by ID
  toggleTrackSelection: (trackId) => {
    const { selectedTracks } = get();
    const newSelection = new Set(selectedTracks);

    if (newSelection.has(trackId)) {
      newSelection.delete(trackId);
    } else {
      newSelection.add(trackId);
    }

    set({ selectedTracks: newSelection });
  },

  // Select all tracks in the current page
  selectAll: () => {
    const { currentPage } = get();
    const allTrackIds = currentPage.items.map((item) => item.track.id);
    set({ selectedTracks: new Set(allTrackIds) });
  },

  // Clear all selections
  clearSelection: () => {
    set({ selectedTracks: new Set<string>() });
  },

  // Set selected date filter (null = show all)
  setSelectedDate: (date) => {
    set({ selectedDate: date });
  },

  // Set loading state
  setLoading: (loading) => {
    set({ isLoading: loading });
  },

  // Set error message
  setError: (error) => {
    set({ error });
  },

  // Reset store to initial state
  reset: () => {
    set({
      currentPage: defaultCurrentPage,
      selectedTracks: new Set<string>(),
      selectedDate: null,
      isLoading: false,
      error: null,
    });
  },
}));
